// /share/: photos and materials (PRD feature 5; ARCHITECTURE.md sections 4, 5 and 10).
//   1. People choose up to limits.maxMaterialFiles images or PDFs (thumbnails for images, remove
//      any file before sending, type and size checked here and again by rules and the server).
//   2. Send: anonymous sign-in, then each file uploads to materials/{uid}/{materialId}/{n}-{safeName}
//      with its base content type and real progress, then submitMaterials records them.
//   3. Uploads are create-only, so a retry never re-sends a file that already arrived: each item
//      remembers its storagePath. Changing the list after a partial send is fine too: new files
//      take a number no earlier attempt used (and if the numbers run out, a fresh materialId).
// Logs (component "materials"): page_ready, not_configured, form_start, files_added,
// files_rejected, file_removed, material_id_reset, validation_failed, submit_start, upload_batch_start,
// upload_file_failed, upload_batch_done, submit_offline, submit_ok, submit_failed,
// fallback_email, share_again. Never names, contacts, descriptions, or file names; fb-storage.js
// adds upload_start/progress/done/failed.
import { logger } from '../lib/log.js';
import { formValues } from '../lib/forms.js';
import { config, limits } from '../lib/config.js';
import { newId } from '../lib/ids.js';
import * as C from './common.js';

const log = logger('materials');

const MAX_FILES = limits.maxMaterialFiles || 10;
const MAX_BYTES = limits.maxFileBytes || 104857600;
const ACCEPT = [...((config.accept && config.accept.image) || ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']),
  ...((config.accept && config.accept.document) || ['application/pdf'])];
const EXT_TYPE = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', heic: 'image/heic', heif: 'image/heif', pdf: 'application/pdf' };
const TYPE_EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'image/heic': 'heic', 'image/heif': 'heif', 'application/pdf': 'pdf' };
const TYPE_ALIAS = { 'image/jpg': 'image/jpeg', 'image/pjpeg': 'image/jpeg', 'image/x-png': 'image/png', 'application/x-pdf': 'application/pdf' };
const KIND_LABEL = { 'image/jpeg': 'JPG photo', 'image/png': 'PNG image', 'image/webp': 'WEBP image', 'image/gif': 'GIF image', 'image/heic': 'HEIC photo', 'image/heif': 'HEIF photo', 'application/pdf': 'PDF file' };

function extOf(name) {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}

/** The base MIME type we will upload with, or null when we cannot accept the file. */
export function fileType(file) {
  let t = String(file.type || '').split(';')[0].trim().toLowerCase();
  t = TYPE_ALIAS[t] || t;
  if (t && ACCEPT.includes(t)) return t;
  // Phones and some computers report HEIC/PDF with an empty or generic type: trust a known extension.
  if (!t || t === 'application/octet-stream') {
    const byExt = EXT_TYPE[extOf(file.name)];
    return byExt && ACCEPT.includes(byExt) ? byExt : null;
  }
  return null;
}

/** "{base}.{ext}" using only [A-Za-z0-9._-], keeping (or adding) the extension, at most 120 chars. */
export function safeName(name, mime) {
  const raw = String(name || '');
  const dot = raw.lastIndexOf('.');
  let base = dot > 0 ? raw.slice(0, dot) : raw;
  let ext = dot > 0 ? raw.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  // The extension follows the type (ARCHITECTURE.md section 10): keep theirs when it matches.
  if (EXT_TYPE[ext] !== mime) ext = TYPE_EXT[mime] || 'bin';
  base = base.normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-{2,}/g, '-').replace(/^[-._]+|[-._]+$/g, '');
  const room = 120 - ext.length - 1;
  base = base.slice(0, room).replace(/[-._]+$/g, '') || 'file';
  return `${base}.${ext}`;
}

export function sizeLabel(bytes) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

export function init() {
  const root = document.querySelector('[data-share]');
  if (!root) return;
  if (!C.isBackendConfigured()) {
    log.info('not_configured', {});
    C.trackMailtos(root, log, 'not_configured');
    return;
  }
  const form = root.querySelector('[data-form]');
  const success = root.querySelector('[data-success]');
  const submitBtn = form.querySelector('[data-submit]');
  const input = form.querySelector('#share-files');
  const pickLabel = form.querySelector('[data-pick-label]');
  const countEl = form.querySelector('[data-file-count]');
  const listEl = form.querySelector('[data-file-list]');
  const rejectsEl = form.querySelector('[data-file-rejects]');
  const rejectsList = form.querySelector('[data-file-rejects-list]');
  const uploadBox = form.querySelector('[data-upload]');
  const uploadBar = form.querySelector('[data-upload-bar]');
  const uploadProgress = form.querySelector('[data-upload-progress]');
  const uploadDetail = form.querySelector('[data-upload-detail]');
  const retryNote = root.querySelector('[data-retry-note]');

  // Every chosen file: { key, file, mime, safe, url, uploaded: { storagePath, sizeBytes } | null, el }
  let items = [];
  let materialId = newId();
  let usedNumbers = new Set(); // n values any upload attempt in this materialId has used
  let uploadUid = null;
  let sending = false;
  let keySeq = 0;

  // Name and size: phones often stamp lastModified with the time of picking, so it cannot be trusted.
  const fileKey = (f) => `${f.name}|${f.size}`;

  function startFresh(reason) {
    materialId = newId();
    usedNumbers = new Set();
    items.forEach((it) => { it.uploaded = null; setStatus(it, '', ''); });
    log.info('material_id_reset', { reason });
  }

  function nextNumber() {
    for (let n = 1; n <= MAX_FILES; n++) if (!usedNumbers.has(n)) return n;
    return null;
  }

  function renderCount() {
    const n = items.length;
    countEl.textContent = n === 0 ? 'No files chosen yet.'
      : `${n} ${n === 1 ? 'file' : 'files'} chosen, ${sizeLabel(items.reduce((s, it) => s + it.file.size, 0))} in all.${n >= MAX_FILES ? ` That is the most we can take at once (${MAX_FILES}).` : ''}`;
    pickLabel.textContent = n === 0 ? 'Choose photos or files' : 'Add more photos or files';
    const full = n >= MAX_FILES || sending;
    input.disabled = full;
    pickLabel.classList.toggle('is-disabled', full);
  }

  function itemEl(it) {
    const li = document.createElement('li');
    li.className = 'oh-file';
    const thumb = document.createElement('span');
    thumb.className = 'oh-file__thumb';
    if (it.mime.startsWith('image/')) {
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.src = it.url;
      // HEIC previews only work in Safari: fall back to a plain badge elsewhere.
      img.addEventListener('error', () => { thumb.replaceChildren(badge(it)); }, { once: true });
      thumb.append(img);
    } else thumb.append(badge(it));
    const info = document.createElement('span');
    info.className = 'oh-file__info';
    const name = document.createElement('span');
    name.className = 'oh-file__name';
    name.textContent = it.file.name || it.safe;
    const meta = document.createElement('span');
    meta.className = 'oh-file__meta';
    meta.textContent = `${KIND_LABEL[it.mime] || 'File'}, ${sizeLabel(it.file.size)}`;
    const status = document.createElement('span');
    status.className = 'oh-file__status';
    status.setAttribute('data-status', '');
    info.append(name, meta, status);
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'btn btn-ghost oh-file__remove';
    rm.textContent = 'Remove';
    rm.setAttribute('aria-label', `Remove ${it.file.name || it.safe}`);
    rm.addEventListener('click', () => removeItem(it));
    li.append(thumb, info, rm);
    return li;
  }

  function badge(it) {
    const b = document.createElement('span');
    b.className = 'oh-file__badge';
    b.textContent = (TYPE_EXT[it.mime] || 'file').toUpperCase();
    b.setAttribute('aria-hidden', 'true');
    return b;
  }

  function setStatus(it, text, state) {
    const s = it.el && it.el.querySelector('[data-status]');
    if (!s) return;
    s.textContent = text;
    s.dataset.state = state || '';
  }

  function showRejects(rejects) {
    if (!rejects.length) { rejectsEl.hidden = true; rejectsList.replaceChildren(); return; }
    rejectsList.replaceChildren(...rejects.map((r) => { const li = document.createElement('li'); li.textContent = r; return li; }));
    rejectsEl.hidden = false;
  }

  function addFiles(fileList) {
    const rejects = [];
    const reasons = { type: 0, size: 0, empty: 0, duplicate: 0, limit: 0 };
    let added = 0;
    for (const f of Array.from(fileList || [])) {
      const label = `"${f.name || 'A file'}"`;
      if (items.some((it) => it.key === fileKey(f))) { reasons.duplicate++; rejects.push(`${label} is already in your list.`); continue; }
      if (items.length >= MAX_FILES) { reasons.limit++; rejects.push(`${label} was not added. You can send up to ${MAX_FILES} files at a time. Send these first, then press Share more.`); continue; }
      const mime = fileType(f);
      if (!mime) { reasons.type++; rejects.push(`${label} is not a photo or a PDF file we can take. We accept JPG, PNG, HEIC, WEBP, GIF, and PDF.`); continue; }
      if (f.size === 0) { reasons.empty++; rejects.push(`${label} is empty. Please choose it again, or try another copy.`); continue; }
      if (f.size > MAX_BYTES) { reasons.size++; rejects.push(`${label} is ${sizeLabel(f.size)}. Each file can be up to ${sizeLabel(MAX_BYTES)}. Try a smaller copy, or ask us to pick the original up.`); continue; }
      const it = { id: ++keySeq, key: fileKey(f), file: f, mime, safe: safeName(f.name, mime), url: mime.startsWith('image/') ? URL.createObjectURL(f) : null, uploaded: null, el: null };
      it.el = itemEl(it);
      items.push(it);
      listEl.append(it.el);
      added++;
    }
    showRejects(rejects);
    renderCount();
    if (added) {
      log.info('files_added', { added, total: items.length, types: [...new Set(items.map((it) => it.mime))], totalBytes: items.reduce((s, it) => s + it.file.size, 0) });
      const box = form.querySelector('[data-field="files"]');
      if (box.classList.contains('field--error')) C.clearErrors(form);
    }
    if (rejects.length) log.warn('files_rejected', reasons);
    const msg = [
      added ? `${added} ${added === 1 ? 'file' : 'files'} added. ${items.length} of ${MAX_FILES} chosen.` : '',
      rejects.length ? `${rejects.length} ${rejects.length === 1 ? 'file was' : 'files were'} not added. ${rejects[0]}` : '',
    ].filter(Boolean).join(' ');
    if (msg) C.announce(msg);
  }

  function removeItem(it) {
    if (sending) return;
    const i = items.indexOf(it);
    if (i < 0) return;
    items.splice(i, 1);
    if (it.url) URL.revokeObjectURL(it.url);
    it.el.remove();
    renderCount();
    log.info('file_removed', { total: items.length, wasUploaded: !!it.uploaded });
    // Keep focus somewhere sensible: the next file's Remove button, else the previous, else the picker.
    const next = items[i] || items[i - 1];
    if (next) next.el.querySelector('.oh-file__remove').focus();
    else if (!input.disabled) input.focus();
    C.announce(`Removed. ${items.length} of ${MAX_FILES} chosen.`);
  }

  input.addEventListener('change', () => {
    addFiles(input.files);
    input.value = ''; // let the same picker add more files, or the same file again after removing it
  });
  input.addEventListener('focus', () => pickLabel.classList.add('has-focus'));
  input.addEventListener('blur', () => pickLabel.classList.remove('has-focus'));

  C.trackStart(form, log);
  C.trackMailtos(root, log, 'page');
  renderCount();

  function read() {
    const v = formValues(form);
    return {
      name: v['owner.name'], email: v['owner.email'], phone: v['owner.phone'], description: v.description,
      pickupRequested: !!v.pickupRequested, returnRequested: !!v.returnRequested,
    };
  }

  function validate(d) {
    const errs = [];
    if (!items.length && !d.pickupRequested) errs.push({ field: 'files', message: 'Please choose at least one photo or file, or ask us to pick your things up.' });
    if (!d.description) errs.push({ field: 'description', message: 'Please tell us a little about what you have, for example "photos from the school\'s first year".' });
    if (!d.name) errs.push({ field: 'owner.name', message: 'Please tell us your name.' });
    errs.push(...C.contactErrors({ email: d.email, phone: d.phone, emailField: 'owner.email', phoneField: 'owner.phone' }));
    return errs;
  }

  function fallbackMailto(d) {
    return C.mailtoHref(C.projectEmail(root), 'Photos and keepsakes for the St. Theresa Oral History Project', C.mailBody(
      'Hello,\n\nI tried to share these on the Oral History website, but they did not go through. I have attached the files to this email.',
      [
        ['What they are', d.description],
        ['Files', items.length ? items.map((it) => it.file.name).join(', ') : ''],
        ['Please arrange a pickup', d.pickupRequested ? 'Yes' : ''],
        ['I would like them back', d.returnRequested ? 'Yes' : ''],
        ['My name', d.name],
        ['My email', d.email],
        ['My phone', d.phone],
      ],
      '(Please attach your photos or files before you send this email.)',
    ));
  }

  function setProgress(fraction, detail) {
    const pct = Math.max(0, Math.min(100, Math.round(fraction * 100)));
    uploadBar.style.width = `${pct}%`;
    uploadProgress.setAttribute('aria-valuenow', String(pct));
    uploadProgress.setAttribute('aria-valuetext', `${pct} percent`);
    if (detail) uploadDetail.textContent = detail;
  }

  /** Upload every file that has not arrived yet. Throws the first failure (after one retry on a fresh number). */
  async function uploadPending(uid) {
    const { uploadFile } = await import('../lib/fb-storage.js');
    const pending = items.filter((it) => !it.uploaded);
    if (!pending.length) return;
    const totalBytes = pending.reduce((s, it) => s + it.file.size, 0) || 1;
    let doneBytes = 0;
    uploadBox.hidden = false;
    log.info('upload_batch_start', { files: pending.length, totalBytes, alreadyUploaded: items.length - pending.length });
    const t0 = performance.now();
    for (let i = 0; i < pending.length; i++) {
      const it = pending[i];
      const label = `Sending file ${i + 1} of ${pending.length}`;
      C.announce(`${label}.`);
      setStatus(it, 'Sending', 'sending');
      let attempt = 0;
      for (;;) {
        const n = nextNumber();
        if (n === null) {
          // Ran out of numbers in this folder (many retries and edits): start a fresh one.
          startFresh('numbers_exhausted');
          throw Object.assign(new Error('restart'), { code: 'oh/restart' });
        }
        usedNumbers.add(n);
        const storagePath = `materials/${uid}/${materialId}/${n}-${it.safe}`;
        try {
          const res = await uploadFile(storagePath, it.file, {
            contentType: it.mime,
            onProgress: ({ bytesTransferred }) => setProgress((doneBytes + bytesTransferred) / totalBytes, `${label}: ${Math.round(((doneBytes + bytesTransferred) / totalBytes) * 100)}%`),
          });
          it.uploaded = { storagePath, sizeBytes: res.sizeBytes };
          doneBytes += it.file.size;
          setProgress(doneBytes / totalBytes, `${label}: sent`);
          setStatus(it, 'Sent', 'done');
          break;
        } catch (err) {
          // A create-only path can be refused because an earlier attempt already wrote it. One
          // retry under a fresh number covers that; a second refusal is a real problem.
          if (err && err.code === 'storage/unauthorized' && attempt === 0) { attempt++; continue; }
          log.error('upload_file_failed', { index: items.indexOf(it) + 1, of: items.length, mimeType: it.mime, bytes: it.file.size, code: err && err.code });
          setStatus(it, 'Not sent yet', 'failed');
          err.failedItem = it;
          throw err;
        }
      }
    }
    log.info('upload_batch_done', { files: pending.length, ms: Math.round(performance.now() - t0) });
  }

  const warnLeave = (e) => { e.preventDefault(); e.returnValue = ''; };

  async function send(d) {
    const { ensureAnonymousUser, callFunction } = await C.backend();
    const user = await ensureAnonymousUser();
    if (uploadUid && uploadUid !== user.uid) startFresh('uid_changed');
    uploadUid = user.uid;
    if (items.some((it) => !it.uploaded)) {
      try { await uploadPending(user.uid); } catch (err) {
        if (err && err.code === 'oh/restart') await uploadPending(user.uid);
        else throw err;
      }
    }
    setProgress(1, 'Saving your details');
    return callFunction('submitMaterials', {
      materialId,
      logSessionId: C.sessionId(),
      owner: { name: d.name, email: d.email || null, phone: d.phone || null },
      description: d.description,
      files: items.map((it) => ({ storagePath: it.uploaded.storagePath, name: (it.file.name || it.safe).slice(0, 200), mimeType: it.mime, sizeBytes: it.uploaded.sizeBytes })),
      pickupRequested: d.pickupRequested,
      returnRequested: d.returnRequested,
    });
  }

  function nextSteps(d, fileCount) {
    return [
      d.pickupRequested ? 'A person on the project team will contact you to arrange a pickup.' : 'A person on the project team will look through what you sent, and may contact you with questions.',
      d.returnRequested ? 'You asked for your things back. We will return them when we are finished.' : null,
      'Nothing is published without the team reviewing it first.',
      fileCount ? null : 'Found some photos after all? Press Share more and send them anytime.',
    ].filter(Boolean);
  }

  function lock(on) {
    sending = on;
    form.querySelectorAll('.oh-file__remove').forEach((b) => { b.disabled = on; });
    renderCount();
    if (on) window.addEventListener('beforeunload', warnLeave);
    else window.removeEventListener('beforeunload', warnLeave);
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (sending) return;
    C.hideSubmitError(root);
    retryNote.hidden = true;
    const d = read();
    const errs = validate(d);
    if (errs.length) {
      log.warn('validation_failed', { fields: errs.map((x) => x.field) });
      C.showErrors(form, errs);
      return;
    }
    C.clearErrors(form);
    if (C.isOffline()) {
      log.warn('submit_offline', {});
      C.showSubmitError(root, { message: C.pageError({ code: 'oh/offline' }), mailto: fallbackMailto(d), log });
      return;
    }
    const restore = C.busy(submitBtn, items.some((it) => !it.uploaded) ? 'Sending your files…' : 'Sending…');
    lock(true);
    setProgress(items.length ? items.filter((it) => it.uploaded).length / items.length : 0, 'Getting ready');
    log.info('submit_start', { files: items.length, pickupRequested: d.pickupRequested, returnRequested: d.returnRequested });
    try {
      const res = await send(d);
      log.info('submit_ok', { materialId: res.materialId, referenceCode: res.referenceCode, files: items.length, duplicate: !!res.alreadyReceived });
      const fileCount = items.length;
      success.querySelector('[data-success-files]').textContent = fileCount
        ? `We received ${fileCount === 1 ? 'your file' : `your ${fileCount} files`}.`
        : 'We have your request for a pickup.';
      success.querySelector('[data-next]').replaceChildren(...nextSteps(d, fileCount).map((t) => { const li = document.createElement('li'); li.textContent = t; return li; }));
      lock(false);
      C.showSuccess(success, { referenceCode: res.referenceCode, hideEls: [form] });
    } catch (err) {
      C.logSubmitFailure(log, err, { uploaded: items.filter((it) => it.uploaded).length, of: items.length });
      lock(false);
      uploadBox.hidden = true;
      const someArrived = items.some((it) => it.uploaded);
      let mapped = null;
      if (err && err.failedItem && err.code === 'storage/unauthorized') {
        C.showSubmitError(root, { message: `We could not accept "${err.failedItem.file.name}". It may be too large, or a kind of file we cannot take. Remove it and press Send again.`, mailto: fallbackMailto(d), log });
      } else {
        mapped = C.handleSubmitError(root, form, err, {
          mailto: fallbackMailto(d), log,
          mapField: (f) => (f && f.startsWith('files') ? 'files' : f),
        });
        // The server rejected an upload it could not find: forget it so Send uploads it again.
        const m = /^files\.(\d+)\./.exec(C.errorField(err) || '');
        const lost = m && items[Number(m[1])];
        if (lost) { lost.uploaded = null; setStatus(lost, 'Not sent yet', 'failed'); }
      }
      if (someArrived && mapped !== 'field') retryNote.hidden = false;
    } finally {
      restore();
    }
  });

  success.querySelector('[data-again]').addEventListener('click', () => {
    log.info('share_again', {});
    items.forEach((it) => { if (it.url) URL.revokeObjectURL(it.url); });
    items = [];
    listEl.replaceChildren();
    showRejects([]);
    startFresh('share_again');
    uploadUid = null;
    form.reset();
    C.clearErrors(form);
    C.hideSubmitError(root);
    uploadBox.hidden = true;
    setProgress(0, 'Getting ready');
    renderCount();
    success.hidden = true;
    form.hidden = false;
    input.focus();
    try { form.scrollIntoView({ block: 'start' }); } catch { /* ignore */ }
    C.announce('The form is empty and ready for more.');
  });

  log.info('page_ready', { maxFiles: MAX_FILES });
}
